import { useCurrency } from "@shared/selectors/globalSelectors";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import React, { useMemo } from "react";

export interface RateDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  selectedPlan?: any;
}

export const RateDetailsDialog: React.FC<RateDetailsDialogProps> = ({
  open,
  onClose,
  selectedPlan,
}) => {
  const currency = useCurrency();
  const rateData = useMemo(() => {
    const rawRates = selectedPlan?.utilityServiceData?.rate || selectedPlan?.utility_service_data?.rate;
    if (!rawRates) {
      return [];
    }

    return Object.entries(rawRates).map(([utilityService, rateInfo]: [string, any]) => {
      // Find average consumption
      const avgConsumption = rateInfo?.avg_consumption ?? rateInfo?.avgConsumption;

      // Find rate value
      let rateValue: any = undefined;
      if (rateInfo?.rate !== undefined && rateInfo?.rate !== null) {
        if (typeof rateInfo.rate === "object") {
          const ratesArray = rateInfo.rate.rate;
          if (Array.isArray(ratesArray) && ratesArray.length > 0) {
            rateValue = ratesArray[0]?.rate;
          } else if (typeof rateInfo.rate.rate === "number" || typeof rateInfo.rate.rate === "string") {
            rateValue = rateInfo.rate.rate;
          }
        } else {
          rateValue = rateInfo.rate;
        }
      }

      return {
        title: utilityService,
        details: [
          rateValue !== undefined && rateValue !== null && { label: "Rate", value: `${currency} ${rateValue}/unit` },
          avgConsumption && { label: "Average Consumption", value: `${avgConsumption} units` },
        ].filter(Boolean),
      };
    });
  }, [selectedPlan, currency]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl p-0 gap-0">
        <DialogHeader className="px-4 py-3 border-b border-gray-200">
          <DialogTitle className="text-xl font-semibold text-gray-900">
            Rate Details
          </DialogTitle>
          <p className="text-xs text-gray-600 mt-0.5">
            Detailed rate information for selected plan
          </p>
        </DialogHeader>

        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {rateData?.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">
              No rate information available for this plan
            </div>
          ) : (
            rateData?.map((utility, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-3">
                <h3 className="text-base font-semibold text-gray-900 mb-2">
                  {utility.title}
                </h3>
                <div className="space-y-1.5">
                  {utility?.details?.map((detail: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between py-1.5">
                      <span className="text-sm text-gray-600">{detail.label}</span>
                      <span className="text-base font-semibold text-gray-900">
                        {detail.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RateDetailsDialog;